import React, { useRef, useCallback } from 'react';
import { CloudUploadRounded as CloudUploadIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { COLOR } from '@src/styles/color';
import { formatFileSize } from '@src/utils/file';
import UploadedFileItem from './UploadedFileItem';
import {
  StyledDropzone,
  StyledDropzoneInner,
  StyledDropzoneTextGroup,
  StyledDropzoneTitle,
  StyledDropzoneDescription,
  StyledDropzoneDescriptionGroup,
  StyledDropzoneErrorText,
  StyledDropzoneErrorDetail,
} from './index.style';

const ProjectFileDropzone = ({
  fileType = [],
  maxSize,
  maxSizeLabel = null,
  file,
  fileDetails,
  fileLoading = false,
  fileLoadingText = '',
  onFiles,
  onRemove,
  error = '',
  errorDetail = '',
  disabled = false,
  renderFileIcon,
  renderFileRightMeta,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);

  const acceptDisplay = fileType.map((type) => `.${type}`).join(', ');

  const isIOS =
    typeof navigator !== 'undefined' &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
  const acceptInput = isIOS
    ? undefined
    : fileType.map((type) => `.${type}`).join(',');

  const handleChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) onFiles?.(files);
    e.target.value = '';
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      if (disabled) return;
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length > 0) onFiles?.(files);
    },
    [onFiles, disabled],
  );

  const handleDragOver = (e) => e.preventDefault();

  const hasFile = Boolean(file);
  const isError = Boolean(error);

  return (
    <StyledDropzone
      isError={isError}
      disabled={disabled}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {!hasFile && !disabled && (
        <input
          ref={inputRef}
          type="file"
          accept={acceptInput}
          onChange={handleChange}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            cursor: 'pointer',
            zIndex: 1,
          }}
        />
      )}
      {hasFile ? (
        <UploadedFileItem
          file={file}
          details={fileDetails}
          loading={fileLoading}
          loadingText={fileLoadingText}
          onRemove={onRemove}
          renderIcon={renderFileIcon}
          renderRightMeta={renderFileRightMeta}
        />
      ) : (
        <StyledDropzoneInner>
          <CloudUploadIcon
            sx={{ width: 32, height: 32, color: COLOR.darkGray }}
          />
          <StyledDropzoneTextGroup>
            <StyledDropzoneTitle>{t('dropFileV2')}</StyledDropzoneTitle>
            {(fileType.length > 0 || maxSize) && (
              <StyledDropzoneDescriptionGroup>
                {fileType.length > 0 && (
                  <StyledDropzoneDescription>
                    {t('supportedFormats', { fileType: acceptDisplay })}
                  </StyledDropzoneDescription>
                )}
                {maxSize && (
                  <StyledDropzoneDescription>
                    {maxSizeLabel ||
                      t('maxFileSize', { size: formatFileSize(maxSize) })}
                  </StyledDropzoneDescription>
                )}
              </StyledDropzoneDescriptionGroup>
            )}
            {isError && (
              <StyledDropzoneErrorText>{error}</StyledDropzoneErrorText>
            )}
            {isError && errorDetail && (
              <StyledDropzoneErrorDetail
                role="presentation"
                onClick={(e) => e.stopPropagation()}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: errorDetail }}
              />
            )}
          </StyledDropzoneTextGroup>
        </StyledDropzoneInner>
      )}
    </StyledDropzone>
  );
};

export default ProjectFileDropzone;
